// Always-visible "Install FLEXX App" button.
// - Hides automatically once installed (display-mode: standalone)
// - Uses native `beforeinstallprompt` when the browser provides it
// - Falls back to platform-specific instructions (iOS Add-to-Home-Screen,
//   desktop browser hint) when the native prompt isn't available
import React, { useEffect, useState } from "react";

const T = {
  gold: "#C9A227", goldLight: "#E8B94F",
  border: "#2a2e36", text: "#EDE8DC", mute: "#888", bg: "#0C0C0C",
};

function detectPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
}

export default function InstallAppButton({ variant = "card" }) {
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(() =>
    typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)")?.matches
  );
  const [showHelp, setShowHelp] = useState(false);
  const [platform] = useState(() => detectPlatform());

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    const onInstalled = () => { setInstalled(true); setInstallEvent(null); setShowHelp(false); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    const mq = window.matchMedia?.("(display-mode: standalone)");
    const onMq = () => setInstalled(mq?.matches || false);
    mq?.addEventListener?.("change", onMq);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      mq?.removeEventListener?.("change", onMq);
    };
  }, []);

  // Tile variant: icon-only button, hides if installed (used in mobile bottom-tab bar)
  if (variant === "tile") {
    if (installed) return null;
    return <InstallTile installEvent={installEvent} setInstallEvent={setInstallEvent} platform={platform} />;
  }

  if (installed) {
    return (
      <div data-testid="install-app-installed" style={wrap}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: T.text, fontWeight: 700, fontSize: 13 }}>✅ FLEXX is installed</div>
          <div style={{ color: T.mute, fontSize: 11, marginTop: 2 }}>You're using the installed app — full offline + push support active.</div>
        </div>
      </div>
    );
  }

  const onClick = async () => {
    if (installEvent) {
      installEvent.prompt();
      try { await installEvent.userChoice; } catch (_) {}
      setInstallEvent(null);
      return;
    }
    setShowHelp(true);
  };

  return (
    <>
      <div data-testid="install-app-card" style={wrap}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: T.text, fontWeight: 700, fontSize: 13 }}>📲 Install FLEXX App</div>
          <div style={{ color: T.mute, fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>
            Faster launch, works offline, gets push alerts when you're away from the desk.
          </div>
        </div>
        <button data-testid="install-app-btn" onClick={onClick} style={primaryBtn}>
          {installEvent ? "Install" : "How?"}
        </button>
      </div>

      {showHelp && <HelpModal platform={platform} onClose={() => setShowHelp(false)} />}
    </>
  );
}

function InstallTile({ installEvent, setInstallEvent, platform }) {
  // First-session-only attention pulse. Persists across tab/page nav within
  // the same browser tab (sessionStorage); fades the moment the student taps.
  const [pulseOff, setPulseOff] = useState(() => {
    if (typeof window === "undefined") return true;
    return sessionStorage.getItem("flexx_install_pulse_seen") === "1";
  });
  const [showHelp, setShowHelp] = useState(false);

  const onClick = async () => {
    // Stop the pulse on first tap — fade-out via .pulse-off class.
    if (!pulseOff) {
      try { sessionStorage.setItem("flexx_install_pulse_seen", "1"); } catch (_) {}
      setPulseOff(true);
    }
    if (installEvent) {
      installEvent.prompt();
      try { await installEvent.userChoice; } catch (_) {}
      setInstallEvent(null);
      return;
    }
    setShowHelp(true);
  };

  return (
    <>
      <button data-testid="mobile-install-tile" onClick={onClick}
        aria-label="Install FLEXX App"
        data-pulse-active={pulseOff ? "false" : "true"}
        className={`flexx-install-pulse${pulseOff ? " pulse-off" : ""}`}
        style={{
          all: "unset",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          padding: "10px 6px 8px",
          cursor: "pointer", color: T.gold,
          minHeight: 48, position: "relative", boxSizing: "border-box",
        }}>
        <span className="flexx-install-icon" style={{ fontSize: 22, lineHeight: 1, filter: `drop-shadow(0 0 6px ${T.gold}55)` }}>📲</span>
        <span style={{ fontSize: 10, fontFamily: "monospace", letterSpacing: 0.5, fontWeight: 700, marginTop: 4 }}>INSTALL</span>
      </button>
      {showHelp && <HelpModal platform={platform} onClose={() => setShowHelp(false)} />}
    </>
  );
}

function HelpModal({ platform, onClose }) {
  return (
    <div data-testid="install-app-help-modal" role="dialog"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 11200,
        background: "rgba(0,0,0,0.86)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "min(440px, 100%)", background: T.bg, border: `1px solid ${T.gold}55`,
        borderRadius: 14, padding: 22, boxShadow: `0 0 50px rgba(201,162,39,0.2)`,
      }}>
        <div style={{ fontFamily: "monospace", color: T.gold, fontSize: 10, letterSpacing: 2, marginBottom: 6 }}>FLEXX ACADEMY</div>
        <h3 style={{ margin: 0, color: T.text, fontSize: 22, fontFamily: "'Bebas Neue','Oswald',sans-serif", letterSpacing: 0.5 }}>INSTALL FLEXX APP</h3>
        <div style={{ marginTop: 14, color: T.text, fontSize: 14, lineHeight: 1.6 }}>
          {platform === "ios" && (
            <>
              <p style={{ margin: "0 0 10px" }}>On iPhone / iPad (Safari):</p>
              <ol data-testid="install-help-ios" style={{ margin: 0, paddingLeft: 22 }}>
                <li style={{ marginBottom: 6 }}>Tap the <b>Share</b> icon at the bottom of Safari (square with up arrow).</li>
                <li style={{ marginBottom: 6 }}>Scroll and choose <b>Add to Home Screen</b>.</li>
                <li>Tap <b>Add</b>. FLEXX will launch like a native app.</li>
              </ol>
              <p style={{ marginTop: 12, color: T.mute, fontSize: 12 }}>iOS does not show an automatic install prompt — you have to add it manually once.</p>
            </>
          )}
          {platform === "android" && (
            <>
              <p style={{ margin: "0 0 10px" }}>On Android (Chrome / Edge):</p>
              <ol data-testid="install-help-android" style={{ margin: 0, paddingLeft: 22 }}>
                <li style={{ marginBottom: 6 }}>Tap the <b>⋮ menu</b> (top-right of the browser).</li>
                <li style={{ marginBottom: 6 }}>Choose <b>Install app</b> or <b>Add to Home Screen</b>.</li>
                <li>Confirm to add FLEXX to your home screen.</li>
              </ol>
              <p style={{ marginTop: 12, color: T.mute, fontSize: 12 }}>If you don't see "Install app", browse a few pages first — Chrome waits for a bit of engagement.</p>
            </>
          )}
          {platform === "desktop" && (
            <>
              <p style={{ margin: "0 0 10px" }}>On Desktop (Chrome / Edge / Brave):</p>
              <ol data-testid="install-help-desktop" style={{ margin: 0, paddingLeft: 22 }}>
                <li style={{ marginBottom: 6 }}>Look for the <b>install icon</b> (⊕ or 🖥️) on the right side of the address bar.</li>
                <li style={{ marginBottom: 6 }}>Or open the browser menu (⋮) → <b>Install FLEXX…</b> / <b>Apps → Install this site as an app</b>.</li>
                <li>Click <b>Install</b> in the dialog.</li>
              </ol>
              <p style={{ marginTop: 12, color: T.mute, fontSize: 12 }}>Firefox and Safari don't support PWA install on desktop — use a Chromium browser.</p>
            </>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button data-testid="install-help-close" onClick={onClose} style={primaryBtn}>Got it</button>
        </div>
      </div>
    </div>
  );
}

const wrap = {
  display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
  padding: "10px 12px", background: "#0d1218",
  border: `1px solid ${T.border}`, borderRadius: 8,
};
const primaryBtn = {
  padding: "8px 14px", borderRadius: 6,
  background: `linear-gradient(135deg, ${T.gold}, ${T.goldLight})`,
  color: "#0a0a0a", border: "none", cursor: "pointer",
  fontWeight: 800, fontFamily: "monospace", letterSpacing: 1, fontSize: 11, textTransform: "uppercase",
  flexShrink: 0,
};
